import { useMemo, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BiTrash, BiCheck } from 'react-icons/bi';
import { FaRegFileExcel, FaRegFilePdf } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmAlert from '../alerts/ConfirmAlert';
import AnimatedLoader from '../ui/AnimatedLoader';

export default function TableLayout({ title, columns, data, setSelectedItem, setShowDeleteConfirm, setShowValideConfirm, loading }) {
    const [dataset, setDataset] = useState(data)

    useEffect(() => {
        setDataset(data)
    }, [data])

    const convertArrayToCSV = (array) => {
        const columnDelimiter = ';';
        const lineDelimiter = '\n';

        if (!array || !array.length) return null;
        const keys = Object.keys(array[0]);

        const result = [keys.join(columnDelimiter), ...array.map(item =>
            keys.map(k => `"${(item[k] ?? '').toString().replace(/"/g, '""')}"`).join(columnDelimiter)
        )];

        return result.join(lineDelimiter);
    };

    // --- CSV EXPORT ---
    const downloadCSV = () => {
        const csv = convertArrayToCSV(dataset);
        if (!csv) return;

        const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `${title || "Données-Exportées"}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- PDF EXPORT ---
    const downloadPDF = () => {
        const doc = new jsPDF();
        const columnsPDF = columns.map(col => col.name);
        const rows = dataset.map((row, index) => [index + 1, ...columnsPDF.map((col) => row[col])]);

        autoTable(doc, {
            head: [columnsPDF],
            body: rows,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255 },
            startY: 20,
            didDrawPage: function (dataset) {
                doc.text('Page ' + doc.internal.getNumberOfPages(), 180, 290);
            },
        });
        doc.save('export.pdf');
    };

    // --- ACTIONS ---
    const actionsMemo = useMemo(() => (
        <div className="btn-group btn-group-sm">
            <button onClick={downloadCSV} className='btn text-white d-flex gap-1 align-items-center btn-success'>
                <FaRegFileExcel size={10} /> <small>CSV</small>
            </button>
            <button onClick={downloadPDF} className='btn text-white d-flex gap-1 align-items-center btn-info'>
                <FaRegFilePdf size={10} /> <small>PDF</small>
            </button>
        </div>
    ), [dataset]);

    // --- CONTEXT ACTIONS ---
    const contextActions = useMemo(() => (
        <div className="btn-group btn-group-sm">
            <button onClick={() => setShowValideConfirm(true)} className='btn text-white d-flex gap-1 align-items-center btn-info'>
                <BiCheck size={10} /> <small>Valider</small>
            </button>
            <button onClick={() => setShowDeleteConfirm(true)} className='btn text-white d-flex gap-1 align-items-center btn-danger'>
                <BiTrash size={10} /> <small>Supprimer</small>
            </button>
        </div>
    ), [dataset]);

    // --- CUSTOM STYLES ---
    const customStyles = {
        headRow: {
            style: {
                fontWeight: 'bold',
                backgroundColor: '#e3e3e3',
                borderTop: '1px solid #ccc',
            },
        },
        headCells: {
            style: {
                borderRight: '1px solid #ccc',
            },
        },
        cells: {
            style: {
                borderRight: '1px solid #ccc',
            },
        },
    };



    return (
        <>
            <motion.div className="table-responsive border-top position-relative" >
                {!loading && dataset.length === 0 ? (
                    <AnimatePresence>
                        <motion.table
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className={'table table-hover table-bordered table-responsive small mt-3'}>
                            <thead >
                                <tr>
                                    {columns.map((col) => (<th style={{ backgroundColor: '#e3e3e3', border: '1px solid #ccc' }} key={col.name}>{col.name}</th>))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td colSpan={columns.length} className="text-center py-5 my-5">
                                        Aucune donnée disponible
                                    </td>
                                </tr>
                            </tbody>
                        </motion.table>
                    </AnimatePresence>
                ) : (
                    <DataTable
                        columns={columns}
                        data={dataset}
                        // progressPending={progressPending}
                        selectableRows
                        onSelectedRowsChange={(state) => setSelectedItem(state.selectedRows)}
                        actions={actionsMemo}
                        contextActions={contextActions}
                        pagination
                        responsive
                        highlightOnHover
                        pointerOnHover
                        striped
                        fixedHeader
                        fixedHeaderScrollHeight="600px"
                        noDataComponent={<div className="text-center py-5 my-5">Aucune donnée disponible</div>}
                        paginationComponentOptions={{
                            rowsPerPageText: 'Lignes par page',
                            rangeSeparatorText: 'sur',
                            noRowsPerPage: false,
                            selectAllRowsItem: false,
                        }}
                        customStyles={customStyles}
                    />
                )}

                {/* Indicateur de chargement */}
                {loading && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                )}
            </motion.div>
{/* 
            <ConfirmAlert
                show={showMultiDeleteConfirm}
                onClose={() => setShowMultiDeleteConfirm(false)}
                onConfirm={handleDeleteData}
                title="Supprimer les données ?"
                message="Voulez-vous vraiment supprimer les données sélectionnées ?"
            /> */}
        </>
    );
}